import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { activeBloodPressureGoal, activeWeightGoal } from '../constants/fitnessProfileGoals';
import {
	apiBloodPressuresToTrend,
	bloodPressureFormFromReading,
	formatRecordedAtLabel,
	latestBloodPressureString,
	replaceFitnessProfileEndpoint,
	replaceFitnessProfileReadingEndpoint,
	sortReadingsNewestFirst,
	todayIsoDate,
	validateBloodPressureInput
} from '../utils/fitnessProfileBloodPressure';
import {
	apiWeightsToTrend,
	latestWeightDisplay,
	validateWeightInput,
	weightFormFromReading
} from '../utils/fitnessProfileWeights';
import { updateFitnessProfile } from '../utils/fitnessProfilesApi';
import {
	bulkUpdateFitnessProfileGoals,
	createFitnessProfileGoals,
	updateFitnessProfileGoalProgress
} from '../utils/fitnessProfileGoalsApi';
import { apiJson, replaceUserEndpoint } from '../utils/userEndpoint';
import {
	buildBloodPressureChartSeries,
	buildWeightChartSeries
} from '../utils/fitnessProfileGoalChart';

const emptyBpForm = () => ({
	date: todayIsoDate(),
	systolic: '',
	diastolic: '',
	editingId: null
});

const emptyWeightForm = () => ({
	date: todayIsoDate(),
	pounds: '',
	editingId: null
});

export default function useFitnessProfileDetailPage(fitnessProfileId) {
	const {
		user,
		csrfToken,
		endpoints,
		syncPushSubscription,
		refreshSessionPreferences,
		activeFitnessProfile,
		setFitnessHubsOnly,
		patchActiveFitnessProfile
	} = useAuth();

	const [profile, setProfile] = useState(null);
	const [profileLoading, setProfileLoading] = useState(true);
	const [profileError, setProfileError] = useState('');

	const [bloodPressureRecords, setBloodPressureRecords] = useState([]);
	const [weightRecords, setWeightRecords] = useState([]);

	const [isBpModalOpen, setIsBpModalOpen] = useState(false);
	const [isBpManageModalOpen, setIsBpManageModalOpen] = useState(false);
	const [bpManageError, setBpManageError] = useState('');
	const [deletingBpId, setDeletingBpId] = useState(null);
	const [bpForm, setBpForm] = useState(emptyBpForm);
	const [bpFormError, setBpFormError] = useState('');
	const [bpSaving, setBpSaving] = useState(false);

	const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
	const [isWeightManageModalOpen, setIsWeightManageModalOpen] = useState(false);
	const [weightManageError, setWeightManageError] = useState('');
	const [deletingWeightId, setDeletingWeightId] = useState(null);
	const [weightForm, setWeightForm] = useState(emptyWeightForm);
	const [weightFormError, setWeightFormError] = useState('');
	const [weightSaving, setWeightSaving] = useState(false);

	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editError, setEditError] = useState('');
	const [editSaving, setEditSaving] = useState(false);

	const [hubsOnlySaving, setHubsOnlySaving] = useState(false);
	const [hubsOnlyError, setHubsOnlyError] = useState('');

	const [goalsModalMode, setGoalsModalMode] = useState(null);
	const [goalsModalError, setGoalsModalError] = useState('');
	const [goalsModalSaving, setGoalsModalSaving] = useState(false);

	const [progressGoal, setProgressGoal] = useState(null);
	const [progressError, setProgressError] = useState('');
	const [progressSaving, setProgressSaving] = useState(false);

	const canUseApi = Boolean(user?.id && endpoints?.get_user_fitness_profile_endpoint);
	const canEditProfile = Boolean(canUseApi && endpoints?.update_user_fitness_profile_endpoint);

	const applyProfile = useCallback((data) => {
		setProfile(data);
		setBloodPressureRecords(data?.blood_pressures || []);
		setWeightRecords(data?.weights || []);
	}, []);

	const loadProfile = useCallback(async () => {
		if (!fitnessProfileId) {
			setProfileLoading(false);
			return;
		}

		if (!canUseApi) {
			setProfile(null);
			setBloodPressureRecords([]);
			setWeightRecords([]);
			setProfileError('');
			setProfileLoading(false);
			return;
		}

		setProfileLoading(true);
		setProfileError('');

		try {
			const url = replaceUserEndpoint(endpoints.get_user_fitness_profile_endpoint, {
				userId: user.id,
				fitnessProfileId
			});
			const data = await apiJson(url, { csrfToken });
			applyProfile(data.data);
		} catch (error) {
			setProfile(null);
			setBloodPressureRecords([]);
			setWeightRecords([]);
			setProfileError(error.message || 'Could not load fitness profile.');
		} finally {
			setProfileLoading(false);
		}
	}, [applyProfile, canUseApi, csrfToken, endpoints, fitnessProfileId, user?.id]);

	const reloadProfileSilently = useCallback(async () => {
		if (!canUseApi || !fitnessProfileId) return;

		try {
			const url = replaceUserEndpoint(endpoints.get_user_fitness_profile_endpoint, {
				userId: user.id,
				fitnessProfileId
			});
			const data = await apiJson(url, { csrfToken });
			applyProfile(data.data);
		} catch {
			// Keep existing UI if background refresh fails; user already saved the reading.
		}
	}, [applyProfile, canUseApi, csrfToken, endpoints, fitnessProfileId, user?.id]);

	useEffect(() => {
		loadProfile();
	}, [loadProfile]);

	const logWeightFromUrlHandled = useRef(false);

	useEffect(() => {
		if (profileLoading || !profile || logWeightFromUrlHandled.current) return;

		const params = new URLSearchParams(window.location.search);
		if (params.get('logWeight') !== '1') return;

		logWeightFromUrlHandled.current = true;
		setWeightForm(emptyWeightForm());
		setWeightFormError('');
		setIsWeightModalOpen(true);

		const url = new URL(window.location.href);
		params.delete('logWeight');
		const newUrl = params.toString() ? `${url.pathname}?${params.toString()}` : url.pathname;
		window.history.replaceState({}, '', newUrl);
	}, [profileLoading, profile]);

	const bloodPressureTrend = useMemo(
		() => apiBloodPressuresToTrend(bloodPressureRecords),
		[bloodPressureRecords]
	);
	const bloodPressureGoal = useMemo(
		() => activeBloodPressureGoal(profile?.goals || []),
		[profile?.goals]
	);
	const bloodPressureChart = useMemo(
		() => buildBloodPressureChartSeries(bloodPressureTrend, bloodPressureGoal),
		[bloodPressureTrend, bloodPressureGoal]
	);
	const bloodPressureDisplay = useMemo(
		() => latestBloodPressureString(bloodPressureTrend) || '—',
		[bloodPressureTrend]
	);
	const bpManageItems = useMemo(
		() =>
			sortReadingsNewestFirst(bloodPressureRecords).map((reading) => ({
				id: reading.id,
				label: formatRecordedAtLabel(reading),
				value: reading.display_value
			})),
		[bloodPressureRecords]
	);

	const weightTrend = useMemo(() => apiWeightsToTrend(weightRecords), [weightRecords]);
	const weightGoal = useMemo(() => activeWeightGoal(profile?.goals || []), [profile?.goals]);
	const weightChart = useMemo(
		() => buildWeightChartSeries(weightTrend, weightGoal),
		[weightTrend, weightGoal]
	);
	const weightDisplay = useMemo(() => latestWeightDisplay(weightTrend) || '—', [weightTrend]);
	const weightManageItems = useMemo(
		() =>
			sortReadingsNewestFirst(weightRecords).map((reading) => ({
				id: reading.id,
				label: formatRecordedAtLabel(reading),
				value: reading.display_value
			})),
		[weightRecords]
	);

	const openEditModal = () => {
		setEditError('');
		setIsEditModalOpen(true);
	};
	const closeEditModal = () => {
		setIsEditModalOpen(false);
		setEditError('');
	};

	const openGoalsModal = () => {
		setGoalsModalError('');
		setGoalsModalMode('manage');
	};
	const openAddGoalsModal = () => {
		setGoalsModalError('');
		setGoalsModalMode('add');
	};
	const closeGoalsModal = () => {
		setGoalsModalMode(null);
		setGoalsModalError('');
	};

	const openGoalProgressModal = (goal) => {
		setProgressError('');
		setProgressGoal(goal);
	};
	const closeGoalProgressModal = () => {
		setProgressGoal(null);
		setProgressError('');
	};

	const handleSaveGoalProgress = async (manualProgressPercent) => {
		if (!canEditProfile || !progressGoal?.id) return;

		setProgressSaving(true);
		setProgressError('');

		try {
			const updated = await updateFitnessProfileGoalProgress({
				userId: user.id,
				fitnessProfileId,
				goalId: progressGoal.id,
				endpoint: endpoints.update_fitness_profile_goal_progress_endpoint,
				csrfToken,
				manualProgressPercent
			});
			applyProfile(updated);
			closeGoalProgressModal();
		} catch (error) {
			setProgressError(error.message || 'Could not update progress.');
		} finally {
			setProgressSaving(false);
		}
	};

	const handleSaveGoalsModal = async (goalsPayload) => {
		if (!canEditProfile || !goalsModalMode) return;

		setGoalsModalSaving(true);
		setGoalsModalError('');

		try {
			const updated =
				goalsModalMode === 'add'
					? await createFitnessProfileGoals({
							userId: user.id,
							fitnessProfileId,
							endpoint: endpoints.create_fitness_profile_goals_endpoint,
							csrfToken,
							goals: goalsPayload
						})
					: await bulkUpdateFitnessProfileGoals({
							userId: user.id,
							fitnessProfileId,
							endpoint: endpoints.bulk_update_fitness_profile_goals_endpoint,
							csrfToken,
							goals: goalsPayload
						});
			applyProfile(updated);
			closeGoalsModal();
		} catch (error) {
			setGoalsModalError(
				error.message ||
					(goalsModalMode === 'add' ? 'Could not add goals.' : 'Could not save goals.')
			);
		} finally {
			setGoalsModalSaving(false);
		}
	};

	const syncHubNavForProfile = useCallback(
		async (profileId, fitnessHubsOnly) => {
			if (Number(activeFitnessProfile?.id) !== Number(profileId)) return;

			setFitnessHubsOnly(Boolean(fitnessHubsOnly));
			patchActiveFitnessProfile({ fitness_hubs_only: fitnessHubsOnly });
			await refreshSessionPreferences();
		},
		[
			activeFitnessProfile?.id,
			patchActiveFitnessProfile,
			refreshSessionPreferences,
			setFitnessHubsOnly
		]
	);

	const handleSaveProfileEdit = async (payload) => {
		if (!canEditProfile) return;

		setEditSaving(true);
		setEditError('');

		try {
			const wasPrimary = Boolean(profile?.is_primary);
			const updated = await updateFitnessProfile({
				userId: user.id,
				fitnessProfileId,
				endpoint: endpoints.update_user_fitness_profile_endpoint,
				csrfToken,
				payload
			});
			applyProfile(updated);
			if (payload.set_as_primary || updated?.is_primary || updated?.is_primary !== wasPrimary) {
				await syncPushSubscription();
			}
			closeEditModal();
		} catch (error) {
			setEditError(error.message || 'Could not update profile.');
		} finally {
			setEditSaving(false);
		}
	};

	const handleUpdateFitnessHubsOnly = async (fitnessHubsOnly) => {
		if (!canEditProfile || profile?.is_primary) return;

		setHubsOnlySaving(true);
		setHubsOnlyError('');

		try {
			const updated = await updateFitnessProfile({
				userId: user.id,
				fitnessProfileId,
				endpoint: endpoints.update_user_fitness_profile_endpoint,
				csrfToken,
				payload: { fitness_hubs_only: fitnessHubsOnly }
			});
			applyProfile(updated);
			await syncHubNavForProfile(
				fitnessProfileId,
				updated?.fitness_hubs_only ?? fitnessHubsOnly
			);
		} catch (error) {
			setHubsOnlyError(error.message || 'Could not update profile settings.');
		} finally {
			setHubsOnlySaving(false);
		}
	};

	const openBpModal = () => {
		setBpForm(emptyBpForm());
		setBpFormError('');
		setIsBpModalOpen(true);
	};
	const closeBpModal = () => {
		setIsBpModalOpen(false);
		setBpFormError('');
	};
	const openBpManageModal = () => {
		setBpManageError('');
		setIsBpManageModalOpen(true);
	};
	const closeBpManageModal = () => {
		setIsBpManageModalOpen(false);
		setBpManageError('');
		setDeletingBpId(null);
	};
	const onBpFormChange = (field, value) => {
		setBpForm((previous) => ({ ...previous, [field]: value }));
		if (bpFormError) setBpFormError('');
	};

	const openBpEditModal = (readingId) => {
		const reading = bloodPressureRecords.find((row) => row.id === readingId);
		if (!reading) return;

		setBpForm(bloodPressureFormFromReading(reading));
		setBpFormError('');
		setIsBpManageModalOpen(false);
		setIsBpModalOpen(true);
	};

	const handleSaveBloodPressure = async () => {
		if (!fitnessProfileId) return;
		if (!bpForm.date) {
			setBpFormError('Choose a date for this reading.');
			return;
		}
		const validation = validateBloodPressureInput(bpForm.systolic, bpForm.diastolic);
		if (!validation.valid) {
			setBpFormError(validation.error);
			return;
		}

		const isEditing = Boolean(bpForm.editingId);
		const endpoint = isEditing
			? endpoints?.update_fitness_profile_blood_pressure_endpoint
			: endpoints?.create_fitness_profile_blood_pressure_endpoint;
		if (!canUseApi || !endpoint) {
			setBpFormError('Sign in to save blood pressure readings.');
			return;
		}

		setBpSaving(true);
		setBpFormError('');

		try {
			const url = isEditing
				? replaceFitnessProfileReadingEndpoint(endpoint, {
						userId: user.id,
						fitnessProfileId,
						readingId: bpForm.editingId
					})
				: replaceFitnessProfileEndpoint(endpoint, {
						userId: user.id,
						fitnessProfileId
					});
			const response = await fetch(url, {
				method: isEditing ? 'PATCH' : 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					blood_pressure: {
						recorded_on: bpForm.date,
						systolic: validation.systolic,
						diastolic: validation.diastolic
					}
				})
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(
					(data.errors || []).join(' ') || data.error || 'Could not save reading.'
				);
			}

			await reloadProfileSilently();
			closeBpModal();
		} catch (error) {
			setBpFormError(error.message || 'Could not save reading.');
		} finally {
			setBpSaving(false);
		}
	};

	const handleDeleteBloodPressure = async (readingId) => {
		if (!fitnessProfileId || !readingId) return;
		if (!canUseApi || !endpoints?.delete_fitness_profile_blood_pressure_endpoint) {
			setBpManageError('Sign in to delete blood pressure readings.');
			return;
		}

		setDeletingBpId(readingId);
		setBpManageError('');

		try {
			const url = replaceFitnessProfileReadingEndpoint(
				endpoints.delete_fitness_profile_blood_pressure_endpoint,
				{ userId: user.id, fitnessProfileId, readingId }
			);
			const response = await fetch(url, {
				method: 'DELETE',
				headers: { 'X-CSRF-Token': csrfToken }
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error((data.errors || []).join(' ') || data.error || 'Could not delete reading.');
			}

			await reloadProfileSilently();
		} catch (error) {
			setBpManageError(error.message || 'Could not delete reading.');
		} finally {
			setDeletingBpId(null);
		}
	};

	const openWeightModal = () => {
		setWeightForm(emptyWeightForm());
		setWeightFormError('');
		setIsWeightModalOpen(true);
	};
	const closeWeightModal = () => {
		setIsWeightModalOpen(false);
		setWeightFormError('');
	};
	const openWeightManageModal = () => {
		setWeightManageError('');
		setIsWeightManageModalOpen(true);
	};
	const closeWeightManageModal = () => {
		setIsWeightManageModalOpen(false);
		setWeightManageError('');
		setDeletingWeightId(null);
	};
	const onWeightFormChange = (field, value) => {
		setWeightForm((previous) => ({ ...previous, [field]: value }));
		if (weightFormError) setWeightFormError('');
	};

	const openWeightEditModal = (readingId) => {
		const reading = weightRecords.find((row) => row.id === readingId);
		if (!reading) return;

		setWeightForm(weightFormFromReading(reading));
		setWeightFormError('');
		setIsWeightManageModalOpen(false);
		setIsWeightModalOpen(true);
	};

	const handleSaveWeight = async () => {
		if (!fitnessProfileId) return;
		if (!weightForm.date) {
			setWeightFormError('Choose a date for this reading.');
			return;
		}
		const validation = validateWeightInput(weightForm.pounds);
		if (!validation.valid) {
			setWeightFormError(validation.error);
			return;
		}

		const isEditing = Boolean(weightForm.editingId);
		const endpoint = isEditing
			? endpoints?.update_fitness_profile_weight_endpoint
			: endpoints?.create_fitness_profile_weight_endpoint;
		if (!canUseApi || !endpoint) {
			setWeightFormError('Sign in to save weight readings.');
			return;
		}

		setWeightSaving(true);
		setWeightFormError('');

		try {
			const url = isEditing
				? replaceFitnessProfileReadingEndpoint(endpoint, {
						userId: user.id,
						fitnessProfileId,
						readingId: weightForm.editingId
					})
				: replaceFitnessProfileEndpoint(endpoint, {
						userId: user.id,
						fitnessProfileId
					});
			const response = await fetch(url, {
				method: isEditing ? 'PATCH' : 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				},
				body: JSON.stringify({
					weight: {
						recorded_on: weightForm.date,
						pounds: validation.pounds
					}
				})
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(
					(data.errors || []).join(' ') || data.error || 'Could not save reading.'
				);
			}

			await reloadProfileSilently();
			closeWeightModal();
		} catch (error) {
			setWeightFormError(error.message || 'Could not save reading.');
		} finally {
			setWeightSaving(false);
		}
	};

	const handleDeleteWeight = async (readingId) => {
		if (!fitnessProfileId || !readingId) return;
		if (!canUseApi || !endpoints?.delete_fitness_profile_weight_endpoint) {
			setWeightManageError('Sign in to delete weight readings.');
			return;
		}

		setDeletingWeightId(readingId);
		setWeightManageError('');

		try {
			const url = replaceFitnessProfileReadingEndpoint(endpoints.delete_fitness_profile_weight_endpoint, {
				userId: user.id,
				fitnessProfileId,
				readingId
			});
			const response = await fetch(url, {
				method: 'DELETE',
				headers: { 'X-CSRF-Token': csrfToken }
			});
			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error((data.errors || []).join(' ') || data.error || 'Could not delete reading.');
			}

			await reloadProfileSilently();
		} catch (error) {
			setWeightManageError(error.message || 'Could not delete reading.');
		} finally {
			setDeletingWeightId(null);
		}
	};

	return {
		profile,
		profileLoading,
		profileError,
		canUseApi,
		canEditProfile,
		weightDisplay,
		weightChart,
		weightGoal,
		weightManageItems,
		bloodPressureDisplay,
		bloodPressureChart,
		bloodPressureGoal,
		bpManageItems,
		editModal: {
			isOpen: isEditModalOpen,
			open: openEditModal,
			close: closeEditModal,
			save: handleSaveProfileEdit,
			saving: editSaving,
			error: editError
		},
		profileSettings: {
			fitnessHubsOnly: Boolean(profile?.fitness_hubs_only),
			onFitnessHubsOnlyChange: handleUpdateFitnessHubsOnly,
			saving: hubsOnlySaving,
			error: hubsOnlyError
		},
		goalsModal: {
			mode: goalsModalMode,
			isOpen: Boolean(goalsModalMode),
			openManage: openGoalsModal,
			openAdd: openAddGoalsModal,
			close: closeGoalsModal,
			save: handleSaveGoalsModal,
			saving: goalsModalSaving,
			error: goalsModalError,
			hasWeightReading: weightRecords.length > 0,
			hasBloodPressureReading: bloodPressureRecords.length > 0
		},
		goalProgressModal: {
			isOpen: Boolean(progressGoal),
			goal: progressGoal,
			open: openGoalProgressModal,
			close: closeGoalProgressModal,
			save: handleSaveGoalProgress,
			saving: progressSaving,
			error: progressError
		},
		bloodPressureLog: {
			isOpen: isBpModalOpen,
			close: closeBpModal,
			open: openBpModal,
			save: handleSaveBloodPressure,
			form: bpForm,
			onFormChange: onBpFormChange,
			error: bpFormError,
			saving: bpSaving
		},
		bloodPressureManage: {
			isOpen: isBpManageModalOpen,
			close: closeBpManageModal,
			open: openBpManageModal,
			items: bpManageItems,
			onEdit: openBpEditModal,
			onDelete: handleDeleteBloodPressure,
			deletingId: deletingBpId,
			error: bpManageError
		},
		weightLog: {
			isOpen: isWeightModalOpen,
			close: closeWeightModal,
			open: openWeightModal,
			save: handleSaveWeight,
			form: weightForm,
			onFormChange: onWeightFormChange,
			error: weightFormError,
			saving: weightSaving
		},
		weightManage: {
			isOpen: isWeightManageModalOpen,
			close: closeWeightManageModal,
			open: openWeightManageModal,
			items: weightManageItems,
			onEdit: openWeightEditModal,
			onDelete: handleDeleteWeight,
			deletingId: deletingWeightId,
			error: weightManageError
		}
	};
}
